import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  Fab,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  Add as AddIcon,
  FilterList as FilterIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { Note, NoteType, NotePriority } from '../../../../types/api';
import { useNotesHistory } from './hooks';
import { NoteCard, ViewNoteDialog } from './components';
import { CreateNoteDialog } from './CreateNoteDialog';
import { useAuthV2 } from '../../../../contexts/AuthContextV2';
import { useTranslation } from '../../../../i18n';

interface NotesListViewProps {
  droneSerial?: string;
  startDate?: string;
  endDate?: string;
  highlightedNoteId?: string | null;
  initialNotes?: Note[];
}

export const NotesListView: React.FC<NotesListViewProps> = ({
  droneSerial,
  startDate,
  endDate,
  highlightedNoteId,
  initialNotes,
}) => {
  const { t } = useTranslation();
  const { user } = useAuthV2();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState<Note | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [expanded, setExpanded] = useState(false); // Default collapsed

  // Filter state
  const [filterType, setFilterType] = useState<NoteType | ''>('');
  const [filterPriority, setFilterPriority] = useState<NotePriority | ''>('');
  const [includeResolved, setIncludeResolved] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Skip fetching when initialNotes is provided and no custom filters are active
  const hasCustomFilters = filterType !== '' || filterPriority !== '' || !includeResolved;
  const skipFetch = !hasCustomFilters && initialNotes !== undefined;

  const {
    notes: fetchedNotes,
    loading,
    error,
  } = useNotesHistory({
    droneSerial: skipFetch ? undefined : droneSerial,
    startDate,
    endDate,
    noteType: filterType || undefined,
    priority: filterPriority || undefined,
    includeResolved,
  });

  const notes = skipFetch ? initialNotes : fetchedNotes;

  const handleNoteClick = (note: Note) => {
    setSelectedNote(note);
    setViewDialogOpen(true);
  };

  // The notes query refreshes itself: each note mutation invalidates
  // statisticsKeys.notes(droneSerial) in onSuccess, triggering a refetch.
  // These callbacks are kept only for any future non-cache side effects.
  const handleNoteCreated = () => {};
  const handleNoteUpdated = () => {};
  const handleNoteDeleted = () => {};

  // Filter notes by search term
  const filteredNotes = notes.filter(
    (note) =>
      note.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
      note.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (note.author && note.author.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const resolvedCount = filteredNotes.filter((note) => note.resolved_at).length;
  const unresolvedCount = filteredNotes.length - resolvedCount;

  return (
    <Box>
      {/* Header - Always Visible */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton onClick={() => setExpanded(!expanded)} size="small" sx={{ p: 0.5 }}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
          <Box>
            <Typography
              variant="h5"
              sx={{ cursor: 'pointer' }}
              onClick={() => setExpanded(!expanded)}
            >
              {droneSerial ? t('notes.notesFor', { serial: droneSerial }) : t('notes.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('notes.summary', {
                total: filteredNotes.length,
                unresolved: unresolvedCount,
                resolved: resolvedCount,
              })}
            </Typography>
          </Box>
        </Box>

        <Box display="flex" gap={1}>
          <Button
            startIcon={<FilterIcon />}
            onClick={() => setShowFilters(!showFilters)}
            variant={showFilters ? 'contained' : 'outlined'}
            disabled={!expanded}
          >
            {t('notes.filters')}
          </Button>
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            onClick={() => setCreateDialogOpen(true)}
            disabled={!droneSerial}
          >
            {t('notes.createNote')}
          </Button>
        </Box>
      </Box>

      {/* Collapsible Content */}
      <Collapse in={expanded}>
        {/* Filters */}
        {showFilters && (
          <Box mb={3} p={2} sx={{ backgroundColor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="subtitle1" gutterBottom>
              {t('notes.filters')}
            </Typography>
            <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
              <TextField
                label={t('notes.search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                sx={{ minWidth: 200 }}
              />

              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>{t('notes.type')}</InputLabel>
                <Select
                  value={filterType}
                  label={t('notes.type')}
                  onChange={(e) => setFilterType(e.target.value as NoteType | '')}
                >
                  <MenuItem value="">{t('notes.allTypes')}</MenuItem>
                  <MenuItem value="general">{t('notes.general')}</MenuItem>
                  <MenuItem value="customer_feedback">{t('notes.customerFeedback')}</MenuItem>
                  <MenuItem value="alert">{t('notes.alert')}</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>{t('notes.priority')}</InputLabel>
                <Select
                  value={filterPriority}
                  label={t('notes.priority')}
                  onChange={(e) => setFilterPriority(e.target.value as NotePriority | '')}
                >
                  <MenuItem value="">{t('notes.allPriorities')}</MenuItem>
                  <MenuItem value="information">{t('notes.information')}</MenuItem>
                  <MenuItem value="critical">{t('notes.critical')}</MenuItem>
                </Select>
              </FormControl>

              <FormControlLabel
                control={
                  <Switch
                    checked={includeResolved}
                    onChange={(e) => setIncludeResolved(e.target.checked)}
                  />
                }
                label={t('notes.includeResolved')}
              />
            </Box>
          </Box>
        )}

        {/* Loading State */}
        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* No Drone Selected */}
        {!droneSerial && !loading && (
          <Box textAlign="center" py={4}>
            <Typography variant="body1" color="text.secondary">
              {t('notes.selectDroneToView')}
            </Typography>
          </Box>
        )}

        {/* Empty State */}
        {droneSerial && !loading && filteredNotes.length === 0 && !error && (
          <Box textAlign="center" py={4}>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              {t('notes.noNotesFound')}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
            >
              {t('notes.createFirstNote')}
            </Button>
          </Box>
        )}

        {/* Notes List */}
        {filteredNotes.length > 0 && (
          <Box>
            {filteredNotes.map((note) => (
              <NoteCard
                key={note.id}
                note={note}
                onClick={handleNoteClick}
                highlighted={note.id === highlightedNoteId}
              />
            ))}
          </Box>
        )}
      </Collapse>

      {/* Floating Action Button for Mobile */}
      {droneSerial && (
        <Fab
          color="primary"
          aria-label={t('notes.addNote')}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            display: { xs: 'flex', md: 'none' },
          }}
          onClick={() => setCreateDialogOpen(true)}
        >
          <AddIcon />
        </Fab>
      )}

      {/* Dialogs */}
      <CreateNoteDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        droneSerial={droneSerial || ''}
        onNoteCreated={handleNoteCreated}
      />

      <ViewNoteDialog
        open={viewDialogOpen}
        onClose={() => {
          setViewDialogOpen(false);
          setSelectedNote(null);
        }}
        note={selectedNote}
        droneSerial={droneSerial || ''}
        currentUserId={user?.id}
        onNoteUpdated={handleNoteUpdated}
        onNoteDeleted={handleNoteDeleted}
      />
    </Box>
  );
};
